import * as arrow from "apache-arrow";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { MUIFlatTableColumn } from "../../components/universal_mui_table/MUIFlatTable";
import { Box } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { calculateColumnWidth } from "../../utils/mui_table_utils";

interface GroupInfo {
  groupKey: string;
  segmentValues: Record<string, any>;
  measureValues: Record<string, any>;
  detailRowIndices: number[]; // Store Arrow row indices
  startRowIndex: number;
}

export const createTransferPlanningMatrix = (args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: DatePivotParams;
  demandWalkConfig?: any;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  theme: Theme;
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
  columnHeaderMap?: Record<string, string>;
  firstMount?: React.MutableRefObject<boolean>;
}) => {
  const {
    arrowTable,
    columns,
    viewParams,
    sortConfig,
    expandedGroups,
    onSort,
    setSortConfig,
    theme,
    firstMount,
    columnSchema,
  } = args;

  if (!arrowTable || !columns || !viewParams?.segments) {
    return null;
  }

  // Set initial sort config if empty and firstMount is provided
  if (firstMount?.current) {
    const initialSortConfig = [
      {
        key: "rebalance_opportunity",
        direction: "desc" as const,
        index: 0,
      },
    ];
    setSortConfig(initialSortConfig);
    firstMount.current = false; // Set to false immediately after setting sort config

    // we are returning early so that initial sortConfig is set
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
      arrowTable: arrow.tableFromArrays({}),
      columnTotals: undefined,
    };
  }

  const availableColumns = columns.map((col) => col.name);

  // Get actual segment column names (use the actual column names, not UI names)
  const actualSegmentNames = viewParams?.segments || [];

  // Define the desired column order using actual column names (with underscores)
  const desiredColumnOrder = [
    "sku",
    "location",
    "starting_inventory",
    "open_purchase_orders",
    "total_committed_supply",
    "starting_days_on_hand",
    "rebalance_opportunity",
    "rebalanced_supply",
    "ending_days_on_hand",
  ];

  // Filter out agg_rebalance_rank from display but keep it for sorting
  const displayColumnsFiltered = availableColumns.filter(
    (col) => col !== "agg_rebalance_rank"
  );

  // Reorder columns according to specified order
  const reorderedColumns: string[] = [];

  // First, add all segment columns in the order they appear in viewParams.segments
  actualSegmentNames.forEach((segment) => {
    if (
      displayColumnsFiltered.includes(segment) &&
      !reorderedColumns.includes(segment)
    ) {
      reorderedColumns.push(segment);
    }
  });

  // Then add columns in the desired order (excluding segments that were already added)
  desiredColumnOrder.forEach((desiredCol) => {
    // Skip if this column is already added (like segments)
    if (reorderedColumns.includes(desiredCol)) {
      return;
    }

    // Try to find the column by exact match first
    let foundColumn = displayColumnsFiltered.find((col) => col === desiredCol);

    // If not found, try case-insensitive match
    if (!foundColumn) {
      foundColumn = displayColumnsFiltered.find(
        (col) => col.toLowerCase() === desiredCol.toLowerCase()
      );
    }

    if (foundColumn && !reorderedColumns.includes(foundColumn)) {
      reorderedColumns.push(foundColumn);
    }
  });

  // Add any remaining columns that weren't in the specified order
  displayColumnsFiltered.forEach((col) => {
    if (!reorderedColumns.includes(col)) {
      reorderedColumns.push(col);
    }
  });

  // Extract all vectors for data processing - keep in Arrow format
  const allVectors = availableColumns.map((colName) => ({
    name: colName,
    vector: arrowTable.getChild(colName),
  }));

  // Get location vector for filtering
  const locationVector = arrowTable.getChild("location");
  if (!locationVector) {
    return null;
  }

  // Use Arrow's built-in methods to filter valid rows efficiently
  const validRowIndices: number[] = [];
  for (let i = 0; i < arrowTable.numRows; i++) {
    const locationValue = locationVector.get(i);
    if (
      locationValue &&
      locationValue !== "" &&
      locationValue !== "null" &&
      locationValue !== "undefined"
    ) {
      validRowIndices.push(i);
    }
  }

  // Group by segments and create subtotals - work with Arrow vectors directly
  const otherVectors = allVectors.filter(
    (v) => !actualSegmentNames.includes(v.name)
  );

  // Use more efficient grouping with pre-computed segment vectors
  const groupedData = new Map<string, number[]>();

  // Get segment vectors once
  const segmentVectors = actualSegmentNames.map((segment) =>
    arrowTable.getChild(segment)
  );
  const validSegmentVectors = segmentVectors.filter((v) => v !== null);

  if (validSegmentVectors.length > 0) {
    // Create composite keys for all valid rows at once
    const compositeKeys = validRowIndices.map((rowIndex) =>
      validSegmentVectors
        .map((vector) => {
          const value = vector!.get(rowIndex);
          return value || "Unknown";
        })
        .join("|")
    );

    // Group by composite key
    compositeKeys.forEach((compositeKey, index) => {
      if (!groupedData.has(compositeKey)) {
        groupedData.set(compositeKey, []);
      }
      groupedData.get(compositeKey)!.push(validRowIndices[index]);
    });
  }

  // Build group index with Arrow vector references
  const groupIndex = new Map<
    string,
    {
      groupKey: string;
      segmentValues: Record<string, any>;
      measureValues: Record<string, any>;
      detailRowIndices: number[]; // Store Arrow row indices instead of converted data
    }
  >();

  groupedData.forEach((rowIndices, groupKey) => {
    if (rowIndices.length === 0) return;

    // Set all segment values from the composite key
    const segmentValues: Record<string, any> = {};
    const segmentValuesArray = groupKey.split("|");
    actualSegmentNames.forEach((segmentName, index) => {
      segmentValues[segmentName] = segmentValuesArray[index] || "Unknown";
    });

    // Calculate subtotals for numeric columns using Arrow vectors
    const measureValues: Record<string, any> = {};
    otherVectors.forEach(({ name, vector }) => {
      if (!vector) return;

      // Skip non-numeric columns for aggregation
      const values: number[] = [];
      rowIndices.forEach((rowIndex) => {
        const value = vector.get(rowIndex);
        if (typeof value === "number") {
          values.push(value);
        } else if (typeof value === "bigint") {
          values.push(Number(value));
        }
      });

      if (values.length > 0) {
        // Use string matching like the original viewer_component
        if (name.toLowerCase().includes("days_on_hand")) {
          // Use average for days on hand fields
          measureValues[name] =
            values.reduce((sum, val) => sum + val, 0) / values.length;
        } else if (name.toLowerCase().includes("rebalance_opportunity")) {
          // Sum absolute values divided by 2 for rebalance opportunity
          measureValues[name] =
            values.reduce((sum, val) => sum + Math.abs(val), 0) / 2;
        } else if (name.toLowerCase().includes("supply")) {
          // Sum supply fields normally (they should balance across the group)
          measureValues[name] = values.reduce((sum, val) => sum + val, 0);
        } else if (name.toLowerCase().includes("days")) {
          // Average for "days" fields
          measureValues[name] =
            values.reduce((sum, val) => sum + val, 0) / values.length;
        } else {
          // Sum for most other fields
          measureValues[name] = values.reduce(
            (sum, val) => sum + Math.abs(val),
            0
          );
        }
      } else {
        measureValues[name] = 0;
      }
    });

    // Special handling: Set starting days on hand to minimum for the segment
    const startingDaysOnHandKey = Object.keys(measureValues).find(
      (key) =>
        key.toLowerCase().includes("starting") &&
        key.toLowerCase().includes("days_on_hand")
    );

    if (startingDaysOnHandKey) {
      const startingDaysVector = arrowTable.getChild(startingDaysOnHandKey);

      if (startingDaysVector) {
        const startingDaysValues = rowIndices
          .map((idx) => startingDaysVector.get(idx))
          .filter((val) => val != null && !isNaN(val))
          .map((val) => Number(val));

        if (startingDaysValues.length > 0) {
          measureValues[startingDaysOnHandKey] = Math.min(
            ...startingDaysValues
          );
        } else {
          measureValues[startingDaysOnHandKey] = 0;
        }
      }
    }

    // Special handling for location - show blank in subtotals
    if (actualSegmentNames.includes("location")) {
      segmentValues.location = "";
    }

    groupIndex.set(groupKey, {
      groupKey,
      segmentValues,
      measureValues,
      detailRowIndices: rowIndices, // Store Arrow row indices
    });
  });

  // Sort the groups
  const sortedGroups = Array.from(groupIndex.values()).sort((a, b) => {
    // If no sort config, maintain original order (let parent handle default)

    // Apply multiple sort criteria in order
    for (const sort of sortConfig) {
      const aValue =
        a.measureValues[sort.key] ?? a.segmentValues[sort.key] ?? 0;
      const bValue =
        b.measureValues[sort.key] ?? b.segmentValues[sort.key] ?? 0;

      // Handle numeric values
      if (typeof aValue === "number" && typeof bValue === "number") {
        const comparison = aValue - bValue;
        if (comparison !== 0) {
          return sort.direction === "asc" ? comparison : -comparison;
        }
      } else {
        // Handle string values
        const aStr = String(aValue);
        const bStr = String(bValue);
        const comparison = aStr.localeCompare(bStr);
        if (comparison !== 0) {
          return sort.direction === "asc" ? comparison : -comparison;
        }
      }
    }

    // If all sort criteria are equal, maintain original order
    return 0;
  });

  // Calculate row counts and positions
  const groupInfos: GroupInfo[] = [];
  let totalRowCount = 0;

  sortedGroups.forEach((groupData) => {
    const isExpanded =
      expandedGroups.has("*") || expandedGroups.has(groupData.groupKey);
    const detailCount = isExpanded ? groupData.detailRowIndices.length : 0;

    groupInfos.push({
      groupKey: groupData.groupKey,
      segmentValues: groupData.segmentValues,
      measureValues: groupData.measureValues,
      detailRowIndices: groupData.detailRowIndices, // Store Arrow row indices
      startRowIndex: totalRowCount,
    });

    totalRowCount += 1 + detailCount; // 1 for group row + detail rows
  });

  // Create MUI-compatible columns with proper MUIFlatTableColumn format
  const muiColumns: MUIFlatTableColumn[] = [
    {
      key: "expand",
      label: "",
      width: 40,
      align: "center",
      isExpandColumn: true,
      sticky: true,
    },
    ...reorderedColumns.map((col) => {
      const sortDirection = sortConfig?.find((s) => s.key === col)?.direction;

      // Check if this is a location or segment column
      const isLocationOrSegment =
        col === "location" || actualSegmentNames.includes(col);

      // Calculate width for location and segment columns using the utility function
      const calculatedWidth = isLocationOrSegment
        ? calculateColumnWidth(col, columnSchema)
        : undefined;

      // Make location and segment columns sticky
      const isSticky = isLocationOrSegment;

      const columnConfig = {
        key: col,
        width: calculatedWidth,
        align: "left" as const,
        sortable: true,
        sticky: isSticky,
        onSort: onSort
          ? (e: React.MouseEvent<HTMLTableCellElement>) => {
              onSort(col, e);
            }
          : undefined,
        sortDirection: sortDirection,
        headerType: new arrow.Utf8(),
      };

      return {
        ...columnConfig,
        renderCell: (row: any, rowIndex: number) => {
          const value = row[col];
          const isGroup = row.type === "group";
          const isDetail = row.type === "detail";
          const isRebalanceOpportunity = col
            .toLowerCase()
            .includes("rebalance_opportunity");

          // Color coding for rebalance opportunity - use background colors like inventory_health
          const getBackgroundColor = () => {
            if (isRebalanceOpportunity && typeof value === "number") {
              if (value > 0) {
                return theme.palette.atomic?.green1; // Green background for positive values
              } else if (value < 0) {
                return theme.palette.atomic?.red1; // Red background for negative values
              }
            }
            return undefined;
          };

          const backgroundColor = getBackgroundColor();

          // Handle location column for group rows - show blank instead of "0"
          const displayValue =
            isGroup && col === "location"
              ? ""
              : isGroup
                ? typeof value === "number"
                  ? value.toLocaleString()
                  : (value ?? "")
                : typeof value === "number"
                  ? value.toLocaleString()
                  : (value ?? "");

          return (
            <Box
              sx={{
                p: 1,
                height: "40px",
                display: "flex",
                alignItems: "center",
                fontWeight:
                  viewParams?.segments?.includes(col) && isGroup
                    ? "bold"
                    : "normal",
                backgroundColor:
                  isGroup && isRebalanceOpportunity
                    ? theme.palette.atomic?.orange1 // Use same yellow as inventory_health
                    : backgroundColor || // Use background colors for rebalance values
                      (isDetail ? theme.palette.action.hover : undefined),
                fontSize: isDetail ? "0.75rem" : "inherit",
                paddingLeft: 1, // Remove indentation - same padding for all rows
                color: "inherit", // Keep text color unchanged
              }}
            >
              {displayValue}
            </Box>
          );
        },
      };
    }),
  ];

  // Lazy row access function - only convert to JS when requested
  const getRow = (rowIndex: number): Record<string, any> => {
    if (rowIndex >= totalRowCount) {
      return {};
    }

    // Find which group this row belongs to
    let groupInfo: GroupInfo | null = null;
    let localRowIndex = 0;

    for (const info of groupInfos) {
      const isExpanded =
        expandedGroups.has("*") || expandedGroups.has(info.groupKey);
      const detailCount = isExpanded ? info.detailRowIndices.length : 0;
      const groupEndIndex = info.startRowIndex + 1 + detailCount;

      if (rowIndex >= info.startRowIndex && rowIndex < groupEndIndex) {
        groupInfo = info;
        localRowIndex = rowIndex - info.startRowIndex;
        break;
      }
    }

    if (!groupInfo) {
      return {};
    }

    // Row 0 is always the group row
    if (localRowIndex === 0) {
      const rowData: Record<string, any> = {
        type: "group",
        groupKey: groupInfo.groupKey,
        isExpanded:
          expandedGroups.has("*") || expandedGroups.has(groupInfo.groupKey),
        groupSize: groupInfo.detailRowIndices.length,
      };

      // Add segment and measure values
      Object.entries(groupInfo.segmentValues).forEach(([key, value]) => {
        rowData[key] = value;
      });
      Object.entries(groupInfo.measureValues).forEach(([key, value]) => {
        rowData[key] = value;
      });

      return rowData;
    }

    // Otherwise it's a detail row (only if expanded)
    const detailIndex = localRowIndex - 1;
    if (detailIndex >= groupInfo.detailRowIndices.length) {
      return {};
    }

    const actualDetailRowIndex = groupInfo.detailRowIndices[detailIndex];

    // Convert Arrow row to JS only when requested
    const rowData: Record<string, any> = {
      type: "detail",
      groupKey: groupInfo.groupKey,
    };

    // Get individual row values for all columns
    reorderedColumns.forEach((col) => {
      const vector = arrowTable.getChild(col);
      if (!vector) return;

      const value = vector.get(actualDetailRowIndex);
      if (typeof value === "number") {
        rowData[col] = value;
      } else if (typeof value === "bigint") {
        rowData[col] = Number(value);
      } else {
        rowData[col] = value || "";
      }
    });

    return rowData;
  };

  return {
    columns: muiColumns,
    getRow,
    rowCount: totalRowCount,
  };
};
